import React, { useState, useEffect, useCallback } from "react";
import { MultiSelectDropdown } from "../../components/dropdown/MultiSelectDropdown";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { PlusIcon, ArrowRight } from "lucide-react";
import { isDefined, sanitizeName } from "../../common/utils";
import { usePremisesStore, usePolicyTagStore } from "../../store/store";
import {
  getFolderTags,
  addTags,
  getUserDirectory,
  createLawsRegulationsTask,
  findOnePolicy,
} from "../../service/api";
import { message } from "antd";
import { useAuth } from "../../auth/AuthProvider";
import { PoliciesModuleConfig, fireSafetyConfig } from "./policiesConfig";

export interface AssignedToUser {
  id: string;
  name: string;
  email: string;
  employeeId?: string;
}

export interface PolicyUploadPayload {
  file: File | null;
  tags: string[];
  expiryDate: string;
  reminder: string;
  region: string;
  premises: string;
  // "standard" variant only (Fire Safety / Hazardous Materials): the file's assignee
  assignedTo?: AssignedToUser | null;
  // "certificate" variant only (Laws & Regulations)
  certificateName?: string;
  issueDate?: string;
  issuedBy?: string;
  isLifetime?: boolean;
}

interface AddFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (payload: PolicyUploadPayload) => void;
  folderId: string;
  config?: PoliciesModuleConfig;
}

const AddFileModal: React.FC<AddFileModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  folderId,
  config = fireSafetyConfig,
}) => {
  const isCertificate = config.variant === "certificate";

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [reminder, setReminder] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);
  const [newTag, setNewTag] = useState<{ label: string; value: string }>({
    label: "",
    value: "",
  });
  const [isNewTag, setIsNewTag] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [assignedTo, setAssignedTo] = useState<string>("");
  const [availableUsers, setAvailableUsers] = useState<AssignedToUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [folderTags, setFolderTags] = useState<string[]>([]);

  // Certificate variant only (Laws & Regulations)
  const [certificateName, setCertificateName] = useState<string>("");
  const [issueDate, setIssueDate] = useState<string>("");
  const [issuedBy, setIssuedBy] = useState<string>("");
  const [createTaskForCertificate, setCreateTaskForCertificate] = useState<boolean>(true);
  const [folderName, setFolderName] = useState<string>("");
  const [isLifetimeCertificate, setIsLifetimeCertificate] = useState<boolean>(false);

  const { allPremises, fetchPremises } = usePremisesStore();
  const {
    policyTags,
    fetchPolicyTags,
    loading: tagsLoading,
    createPolicyTags,
  } = usePolicyTagStore();
  const { user } = useAuth();

  const fetchUsersByPremises = useCallback(
    async (premisesId: string) => {
      if (isCertificate) {
        if (!premisesId || premisesId === "N/A") {
          setAvailableUsers([]);
          return;
        }
        try {
          setLoadingUsers(true);
          const response = await getUserDirectory();
          const users = response.data?.data || [];
          const filteredUsers = users.filter((u: any) => {
            if (!u.premise) return false;
            if (Array.isArray(u.premise)) {
              return u.premise.some((p: any) => {
                if (typeof p === "string") return p === premisesId;
                if (p && typeof p === "object" && p.$oid) return p.$oid === premisesId;
                return false;
              });
            } else if (typeof u.premise === "string") {
              return u.premise === premisesId;
            } else if (u.premise && typeof u.premise === "object" && u.premise.$oid) {
              return u.premise.$oid === premisesId;
            }
            return false;
          });
          const transformedUsers: AssignedToUser[] = filteredUsers.map((u: any) => ({
            id: u._id || u.id,
            name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.name || "Unknown",
            email: u.email || "",
            employeeId: u.employeeId || "",
          }));
          setAvailableUsers(transformedUsers);
        } catch (error: any) {
          console.error("Error fetching users by premises:", error);
          message.error("Failed to fetch users for selected premises");
          setAvailableUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      } else {
        if (!premisesId || premisesId === "N/A" || premisesId === "ALL Premises") {
          setAvailableUsers([]);
          return;
        }
        try {
          setLoadingUsers(true);
          const response = await getUserDirectory();
          const users = response.data?.data || response.data?.message || [];
          const filteredUsers = users.filter((u: any) => {
            if (!u.premise) return false;
            if (Array.isArray(u.premise)) {
              return u.premise.some((p: any) => {
                if (typeof p === "string") return p === premisesId;
                if (p && typeof p === "object" && p.$oid) return p.$oid === premisesId;
                return p?._id === premisesId || p?.id === premisesId;
              });
            } else if (typeof u.premise === "string") {
              return u.premise === premisesId;
            } else if (u.premise && typeof u.premise === "object") {
              return (
                u.premise.$oid === premisesId ||
                u.premise._id === premisesId ||
                u.premise.id === premisesId
              );
            }
            return false;
          });
          const transformedUsers: AssignedToUser[] = filteredUsers.map((u: any) => ({
            id: u._id || u.id,
            name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.name || "Unknown",
            email: u.email || "",
            employeeId: u.employeeId || "",
          }));
          setAvailableUsers(transformedUsers);
        } catch (error: any) {
          console.error("Error fetching users by premises:", error);
          setAvailableUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      }
    },
    [isCertificate]
  );

  // Fetch folder tags
  const fetchFolderTags = async (id: string) => {
    try {
      const response = await getFolderTags(id);
      const folderTagsData = response.data.message || [];
      setFolderTags(folderTagsData);
    } catch (error) {
      console.error("Error fetching folder tags:", error);
      setFolderTags([]);
    }
  };

  // Fetch folder name when component opens (certificate variant only, used in task description)
  const fetchFolderName = async (id: string) => {
    try {
      const response = await findOnePolicy(id);
      const responseData = response?.data?.data as any;
      const directData = response?.data as any;
      const messageData = response?.data?.message as any;

      let name = "";
      if (responseData?.folderName) name = responseData.folderName;
      else if (messageData?.folderName) name = messageData.folderName;
      else if (directData?.folderName) name = directData.folderName;
      else if (responseData?.name) name = responseData.name;
      else if (messageData?.name) name = messageData.name;
      else if (directData?.name) name = directData.name;

      setFolderName(name || "Unknown Folder");
    } catch (error) {
      console.error("Error fetching folder name:", error);
      setFolderName("Error Loading Folder");
    }
  };

  useEffect(() => {
    fetchPremises();
  }, []);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const success = await fetchPolicyTags();
      if (!success) {
        setErrorMessage("Failed to fetch policy tags from server");
        return;
      }

      const { policyTags: storeTags } = usePolicyTagStore.getState();
      const fetchedTags = storeTags || [];

      if (!Array.isArray(fetchedTags)) {
        console.error("Expected an array of tags, got:", fetchedTags);
        setErrorMessage("Invalid tag data from server");
        return;
      }

      setTags(
        fetchedTags.map((tag: any) => ({
          label: tag.policytagName || tag.policytagID,
          value: tag.id || tag.policytagID || tag.policytagName,
        }))
      );
    } catch (error: any) {
      console.error(
        "Error fetching tags:",
        error.message,
        error.response?.data
      );
      setErrorMessage(
        "Failed to load tags: " + (error.response?.data?.error || error.message)
      );
    } finally {
      setLoading(false);
    }
  }, [fetchPolicyTags]);

  // Update users when premises selection changes
  useEffect(() => {
    if (isCertificate) {
      if (selectedPremises && createTaskForCertificate) {
        fetchUsersByPremises(selectedPremises);
      } else {
        setAvailableUsers([]);
      }
    } else {
      if (selectedPremises) {
        fetchUsersByPremises(selectedPremises);
      } else {
        setAvailableUsers([]);
      }
    }
  }, [selectedPremises, isCertificate, createTaskForCertificate, fetchUsersByPremises]);

  // Reset assigned user when premises changes
  useEffect(() => {
    setAssignedTo("");
  }, [selectedPremises]);

  useEffect(() => {
    if (isOpen && folderId) {
      setSelectedTags([]);
      setExpiryDate("");
      setReminder("");
      setFile(null);
      setNewTag({ label: "", value: "" });
      setIsNewTag(false);
      setErrorMessage("");
      setSelectedRegion("");
      setSelectedPremises("");
      setAssignedTo("");
      setAvailableUsers([]);
      if (isCertificate) {
        setCertificateName("");
        setIssueDate("");
        setIssuedBy("");
        setCreateTaskForCertificate(true);
        setFolderName("");
        setIsLifetimeCertificate(false);
        Promise.all([fetchTags(), fetchFolderName(folderId), fetchFolderTags(folderId)]);
      } else {
        Promise.all([fetchTags(), fetchFolderTags(folderId)]);
      }
    }
  }, [isOpen, folderId, fetchTags, isCertificate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setErrorMessage("");
    }
  };

  const handleAddTag = async () => {
    if (!isDefined(newTag.label) || !isDefined(newTag.value)) {
      setErrorMessage("Tag cannot be empty");
      return;
    }

    setLoading(true);
    try {
      // Create folder-specific tag only
      await addTags({ folderId, newTags: [newTag.value.trim()] });
      // Add the new folder tag to selected tags
      setSelectedTags((prevSelected) => [...prevSelected, newTag.value.trim()]);
      // Refresh folder tags
      await fetchFolderTags(folderId);
      if (isCertificate) {
        message.success("Folder-specific tag added successfully");
      }
      setErrorMessage(""); // Clear any previous errors

      setNewTag({ label: "", value: "" });
      setIsNewTag(false);
    } catch (error: any) {
      console.error(
        "Error adding folder tag:",
        error.message,
        error.response?.data
      );
      setErrorMessage(
        "Failed to add folder tag: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (isCertificate) {
      if (!certificateName.trim()) {
        setErrorMessage("Certificate/Reference name cannot be empty");
        return;
      }
      if (!issueDate) {
        setErrorMessage("Issue date cannot be empty");
        return;
      }
      if (file && file.size > 5 * 1024 * 1024) {
        setErrorMessage("File size cannot be more than 5MB");
        return;
      }
      if (createTaskForCertificate && !selectedPremises) {
        setErrorMessage("Please select premises to create a task");
        return;
      }
      if (createTaskForCertificate && !assignedTo) {
        setErrorMessage("Please select an assignee for the task");
        return;
      }
      if (createTaskForCertificate && !user) {
        setErrorMessage("User authentication required to create tasks");
        return;
      }

      try {
        setLoading(true);

        if (createTaskForCertificate) {
          const selectedUser = availableUsers.find((u) => u.id === assignedTo);
          const userName = selectedUser ? selectedUser.name : "System";
          const userEmail = selectedUser ? selectedUser.email : "";

          const expiryText = isLifetimeCertificate
            ? "This is a lifetime certificate (no expiry date)."
            : expiryDate
            ? `Expires on ${expiryDate}`
            : "No expiry date set";
          const reminderDetails = isLifetimeCertificate
            ? "No reminders configured for lifetime certificates."
            : reminder
            ? `Reminder set: ${reminder}`
            : "No reminder set.";

          const taskData = {
            lawId: folderId,
            lawName: folderName,
            // Kept as its own field (not just embedded in taskTitle) so a certificate delete can cascade-match its task
            certificateName,
            taskType: "Policy" as const,
            taskTitle: `Certificate Management: ${certificateName}`,
            description: `Certificate "${certificateName}" requires attention. ${expiryText} Issued by ${issuedBy || "N/A"} on ${issueDate || "N/A"}. ${reminderDetails}`,
            frequency: "One Time" as const,
            dueDate: !isLifetimeCertificate
              ? expiryDate || new Date().toISOString()
              : new Date().toISOString(),
            reminderDate: !isLifetimeCertificate
              ? expiryDate || new Date().toISOString()
              : new Date().toISOString(),
            priority: (!isLifetimeCertificate && expiryDate ? "High" : "Medium") as
              | "High"
              | "Medium",
            status: "Pending" as "Pending" | "In Progress" | "Completed" | "Overdue",
            assignedTo: {
              name: userName,
              email: userEmail || "system@example.com",
            },
            attachments: [],
            remarks: `Certificate issued by ${issuedBy || "N/A"} on ${issueDate || "N/A"}. ${expiryText} Assigned to: ${userName} (${userEmail || "N/A"}). ${reminderDetails}`,
            reminderSet: true,
            premisesId: selectedPremises || "default-premises-id",
            lastUpdated: new Date().toISOString(),
            tenantId: user?.tenantId || user?.tenant_id || "default-tenant-id",
            createdBy: user?.id || "system",
            createdAt: new Date().toISOString(),
          };

          try {
            await createLawsRegulationsTask(taskData);
            message.success("Certificate uploaded and task created successfully");
          } catch (taskError: any) {
            console.error("Failed to create task, but continuing with certificate upload:", taskError);
            message.warning("Certificate uploaded successfully, but failed to create task");
          }
        } else {
          message.success("Certificate uploaded successfully");
        }

        onUpload({
          file,
          tags: selectedTags,
          expiryDate: isLifetimeCertificate ? "" : expiryDate,
          reminder: isLifetimeCertificate ? "" : reminder,
          region: selectedRegion,
          premises: selectedPremises,
          certificateName,
          issueDate,
          issuedBy,
          isLifetime: isLifetimeCertificate,
        });

        onClose();
      } catch (error: any) {
        console.error("Error in certificate upload process:", error);
        setErrorMessage("Failed to process certificate upload");
      } finally {
        setLoading(false);
      }
    } else {
      if (file && file.size > 5 * 1024 * 1024) {
        setErrorMessage("File size cannot be more than 5MB");
        return;
      }
      const selectedUser = assignedTo
        ? availableUsers.find((u) => u.id === assignedTo) || null
        : null;
      onUpload({
        file,
        tags: selectedTags,
        expiryDate,
        reminder,
        region: selectedRegion,
        premises: selectedPremises,
        assignedTo: selectedUser,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 ${
        isCertificate ? "z-50" : "z-[1000001]"
      }`}
    >
      <div
        className={`bg-white rounded-lg shadow-lg w-[800px] ${
          isCertificate ? "max-h-[90vh] overflow-y-auto" : ""
        }`}
      >
        <div className="p-6 overflow-hidden">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">
              {isCertificate ? "Add Certificate/Reference" : "Add Files"}
            </h2>
            <hr className="my-2 border-gray-300" />
          </div>

          {isCertificate && (
            <>
              <div className="mb-4">
                <div className="w-full">
                  <label className="block text-gray-600 font-semibold">
                    Certificate/Reference Name *
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-md p-2"
                    placeholder="Enter certificate/reference name"
                    value={certificateName}
                    onChange={(e) => setCertificateName(sanitizeName(e.target.value))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="w-full">
                  <label className="block text-gray-600 font-semibold">
                    Issue Date *
                  </label>
                  <input
                    type="date"
                    className="w-full border rounded-md p-2"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </div>
                <div className="w-full">
                  <label className="block text-gray-600 font-semibold mr-2">
                    Issued By
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-md p-2"
                    placeholder="Enter issued by"
                    value={issuedBy}
                    onChange={(e) => setIssuedBy(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="w-full">
              <label className="block text-gray-600 font-semibold">
                Tags
                <span
                  className="uppercase float-end cursor-pointer text-teal-500"
                  style={{ fontSize: "12px" }}
                  onClick={() => setIsNewTag(!isNewTag)}
                >
                  <PlusIcon className="w-4 inline" /> Add Tags
                </span>
              </label>
              {isNewTag && (
                <div className="flex">
                  <input
                    type="text"
                    className={`w-full border rounded-md p-2 ${
                      loading ? "opacity-50" : ""
                    }`}
                    value={newTag.label}
                    onChange={(e) =>
                      setNewTag({
                        label: e.target.value,
                        value: e.target.value,
                      })
                    }
                    disabled={loading}
                    placeholder="Enter folder-specific tag name"
                  />
                  <div
                    className={`p-2 bg-teal-500 ${loading ? "opacity-50" : ""}`}
                    onClick={loading ? undefined : handleAddTag}
                  >
                    <ArrowRight className="text-white" />
                  </div>
                </div>
              )}

              {!isNewTag && (
                <MultiSelectDropdown
                  key={`${tags.length}-${folderTags.length}`}
                  options={[
                    ...tags.map((tag) => ({ ...tag, type: "master" })),
                    ...folderTags.map((tag) => ({
                      label: tag,
                      value: tag,
                      type: "folder",
                    })),
                  ]}
                  selectedValues={selectedTags}
                  onChange={setSelectedTags}
                  placeholder={
                    loading || tagsLoading
                      ? "Loading tags..."
                      : tags.length === 0 && folderTags.length === 0
                      ? "No tags available"
                      : "Select Tags"
                  }
                />
              )}
            </div>
            <div className="w-full">
              <label className="block text-gray-600 font-semibold">
                Region
              </label>
              <Dropdown
                selectedValue={selectedRegion}
                onChange={setSelectedRegion}
                type="region"
              />
            </div>
            <div className="w-full">
              <label className="block text-gray-600 font-semibold">
                Premises
              </label>
              <Dropdown
                selectedValue={selectedPremises}
                onChange={setSelectedPremises}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </div>
          </div>

          {isCertificate && (
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={isLifetimeCertificate}
                  onChange={(e) => {
                    setIsLifetimeCertificate(e.target.checked);
                    if (e.target.checked) {
                      setExpiryDate("");
                      setReminder("");
                      setCreateTaskForCertificate(false);
                      setAssignedTo("");
                      setAvailableUsers([]);
                    }
                    if (!e.target.checked) {
                      setCreateTaskForCertificate(true);
                    }
                  }}
                />
                <span className="text-gray-600 font-semibold">
                  Certificate is lifetime (no expiry date)
                </span>
              </label>
              {isLifetimeCertificate && (
                <p className="text-sm text-gray-500 ml-6">
                  Expiry date, reminders, and task assignments are not required for lifetime certificates.
                </p>
              )}
            </div>
          )}

          {(!isCertificate || !isLifetimeCertificate) && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="w-full">
                <label className="block text-gray-600 font-semibold">
                  Expiry Date
                </label>
                <input
                  type="date"
                  className="w-full border rounded-md p-2"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-gray-600 font-semibold">
                  Reminder Days Before
                </label>
                <select
                  className="w-full border rounded-md p-2"
                  value={isCertificate ? undefined : reminder}
                  onChange={(e) => setReminder(e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="1 Week">1 Week</option>
                  <option value="2 Weeks">2 Weeks</option>
                </select>
              </div>
            </div>
          )}

          {!isCertificate && (
            <div className="mb-4">
              <label className="block text-gray-600 font-semibold">
                Assigned To
              </label>
              <select
                className="w-full border rounded-md p-2"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                disabled={loadingUsers}
                aria-label="Assign file to user"
              >
                <option value="">
                  {selectedPremises
                    ? "Select an assignee"
                    : "Select premises first"}
                </option>
                {loadingUsers ? (
                  <option disabled>Loading users...</option>
                ) : availableUsers.length > 0 ? (
                  availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                      {u.employeeId ? ` - ${u.employeeId}` : ""}
                    </option>
                  ))
                ) : selectedPremises ? (
                  <option disabled>No users available for selected premises</option>
                ) : null}
              </select>
              {assignedTo && availableUsers.length > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  Selected: {availableUsers.find((u) => u.id === assignedTo)?.name}{" "}
                  ({availableUsers.find((u) => u.id === assignedTo)?.email})
                </p>
              )}
            </div>
          )}

          {isCertificate && !isLifetimeCertificate && (
            <>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={createTaskForCertificate}
                    onChange={(e) => setCreateTaskForCertificate(e.target.checked)}
                  />
                  <span className="text-gray-600 font-semibold">
                    Create task for certificate management
                  </span>
                </label>
                <p className="text-sm text-gray-500 ml-6">
                  This will automatically create a task in the Tasks module for tracking certificate renewals and compliance.
                  {createTaskForCertificate && (
                    <span className="block mt-1 text-blue-600">
                      ✓ Task will be assigned to a user from the selected premises
                    </span>
                  )}
                </p>
              </div>

              {createTaskForCertificate && (
                <div className="mb-4">
                  <label className="block text-gray-600 font-semibold">
                    Assigned To
                  </label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    disabled={loadingUsers}
                  >
                    <option value="">Select an assignee</option>
                    {loadingUsers ? (
                      <option disabled>Loading users...</option>
                    ) : availableUsers.length > 0 ? (
                      availableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email}) - {u.employeeId}
                        </option>
                      ))
                    ) : (
                      <option disabled>No users available for selected premises</option>
                    )}
                  </select>

                  {assignedTo && availableUsers.length > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>Selected User:</strong> {availableUsers.find((u) => u.id === assignedTo)?.name}
                      </p>
                      <p className="text-sm text-blue-700">
                        <strong>Email:</strong> {availableUsers.find((u) => u.id === assignedTo)?.email}
                      </p>
                      <p className="text-sm text-blue-700">
                        <strong>Employee ID:</strong> {availableUsers.find((u) => u.id === assignedTo)?.employeeId}
                      </p>
                    </div>
                  )}

                  {availableUsers.length === 0 && !loadingUsers && selectedPremises && (
                    <p className="text-sm text-orange-500 mt-1">
                      No users found for the selected premises. The task will be assigned to "System".
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          <div>
            <label className="font-semibold">
              {isCertificate ? "Upload File" : "Upload Files"}
            </label>
            <div className="mb-4 border-dashed border-2 p-6 text-center cursor-pointer">
              <input
                type="file"
                className="hidden"
                id="fileInput"
                accept=".docx,.pdf,.txt,.jpg,.png,.ppt,.pptx"
                onChange={handleFileChange}
              />
              <label htmlFor="fileInput" className="cursor-pointer">
                {file ? (
                  <p className="text-gray-600">{file.name}</p>
                ) : (
                  "Drag & Drop Your Files Here Or Click"
                )}
              </label>
              <p className="text-sm text-gray-500 mt-2">
                {isCertificate
                  ? "Supported formats: DOCX, PDF, TXT, JPG, PNG, PPT, PPTX (Max: 5MB)"
                  : "Supported formats: DOCX, XLSX, PDF, TXT, JPG, PNG, PPT, PPTX (Max: 5MB)"}
              </p>
            </div>
            {isDefined(errorMessage) && (
              <p className="text-red-600 text-sm">{errorMessage}</p>
            )}
          </div>
        </div>
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-400 text-white rounded-full"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-teal-500 text-white rounded-full"
            disabled={loading}
          >
            {isCertificate
              ? loading
                ? createTaskForCertificate
                  ? "Creating Task..."
                  : "Uploading..."
                : "Upload"
              : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFileModal;
